// src/components/panel/SalesByCategory.js
import React, { useState, useEffect, useCallback } from 'react';
import { TagsOutlined } from '@ant-design/icons';
import { Spin, Dropdown } from 'antd';
import analyticsV2Service, { getDateRanges } from '../../services/analyticsV2Service';

// Color palette for categories
const CATEGORY_COLORS = [
    'linear-gradient(90deg, #1890ff 0%, #40a9ff 100%)', // Blue
    'linear-gradient(90deg, #52c41a 0%, #73d13d 100%)', // Green
    'linear-gradient(90deg, #faad14 0%, #ffc53d 100%)', // Orange
    'linear-gradient(90deg, #722ed1 0%, #9254de 100%)', // Purple
    'linear-gradient(90deg, #eb2f96 0%, #f759ab 100%)', // Pink
    'linear-gradient(90deg, #13c2c2 0%, #36cfc9 100%)', // Cyan
    'linear-gradient(90deg, #fa8c16 0%, #ffa940 100%)', // Gold
    'linear-gradient(90deg, #2f54eb 0%, #597ef7 100%)', // Geekblue
    'linear-gradient(90deg, #f5222d 0%, #ff4d4f 100%)', // Red
    'linear-gradient(90deg, #a0d911 0%, #bae637 100%)', // Lime
];

function SalesByCategory() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [period, setPeriod] = useState('day');
    const [monthOption, setMonthOption] = useState('currentMonth'); // 'currentMonth' or 'prevMonth'

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            
            // Get date ranges
            const ranges = getDateRanges();
            
            // Determine which date range to use based on period and month option
            let dateRange;
            if (period === 'day') {
                dateRange = ranges.today;
            } else if (period === 'week') {
                dateRange = ranges.week;
            } else if (period === 'month') {
                dateRange = monthOption === 'currentMonth' ? ranges.currentMonth : ranges.prevMonth;
            }
            
            const response = await analyticsV2Service.getSalesByCategory(dateRange.start, dateRange.end);
            
            if (response.success && response.data) {
                setData(response.data.categories || []);
            } else {
                setData([]);
            }
            setError(null);
        } catch (err) {
            console.error('Error loading sales by category:', err);
            setError('Error al cargar ventas por categoría');
        } finally {
            setLoading(false);
        }
    }, [period, monthOption]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handlePeriodChange = (newPeriod) => {
        setPeriod(newPeriod);
        if (newPeriod === 'month') {
            setMonthOption('currentMonth');
        }
    };

    const handleMonthOptionChange = (option) => {
        setMonthOption(option);
    };

    // Month dropdown menu items
    const monthMenuItems = {
        items: [
            {
                key: 'currentMonth',
                label: 'Este Mes',
                onClick: () => handleMonthOptionChange('currentMonth')
            },
            {
                key: 'prevMonth',
                label: 'Mes Pasado',
                onClick: () => handleMonthOptionChange('prevMonth')
            }
        ]
    };

    if (loading) {
        return (
            <div className='card'>
                <div className='card-inner'>
                    <TagsOutlined className='card_icon' style={{ fontSize: '38px' }} />
                    <h2>VENTAS POR CATEGORÍA</h2>
                </div>
                <br />
                <div className='card-content-center'>
                    <Spin tip="Cargando categorías..." />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className='card'>
                <div className='card-inner'>
                    <TagsOutlined className='card_icon' style={{ fontSize: '38px' }} />
                    <h2>VENTAS POR CATEGORÍA</h2>
                </div>
                <br />
                <div className='card-content-center' style={{ color: '#ff4d4f' }}>
                    {error}
                </div>
            </div>
        );
    }

    const totalRevenue = data.reduce((sum, item) => sum + (parseFloat(item.totalRevenue) || 0), 0);

    return (
        <div className='card'>
            <div className='card-inner'>
                <TagsOutlined className='card_icon' style={{ fontSize: '38px' }} />
                <h2>VENTAS POR CATEGORÍA</h2>
            </div>
            
            {/* Period selector buttons */}
            <div className='category-period-selector' style={{ marginTop: '8px' }}>
                <button 
                    className={`category-period-btn ${period === 'day' ? 'active' : ''}`}
                    onClick={() => handlePeriodChange('day')}
                >
                    Hoy
                </button>
                <button 
                    className={`category-period-btn ${period === 'week' ? 'active' : ''}`}
                    onClick={() => handlePeriodChange('week')}
                >
                    Semana
                </button>
                <Dropdown 
                    menu={monthMenuItems} 
                    trigger={['click']}
                    placement="bottom"
                >
                    <button 
                        className={`category-period-btn ${period === 'month' ? 'active' : ''}`}
                        onClick={(e) => {
                            if (period !== 'month') {
                                handlePeriodChange('month');
                            }
                        }}
                    >
                        {period === 'month' 
                            ? (monthOption === 'currentMonth' ? 'Este Mes' : 'Mes Pasado')
                            : 'Mes'
                        } ▾
                    </button>
                </Dropdown>
            </div>
            <div className='category-list'>
                {data.length === 0 ? (
                    <div className='empty-state'>No hay ventas para este período</div>
                ) : (
                    data.map((category, index) => {
                        const percentage = category.percentage || 0;
                        const barColor = CATEGORY_COLORS[index % CATEGORY_COLORS.length];
                        
                        return (
                            <div key={index} className='category-item'>
                                <div className='category-bar-wrapper'>
                                    <span className='category-bar-label-outside'>{category.category || 'Sin categoría'}</span>
                                    <div className='category-bar-container'>
                                        <div 
                                            className='category-bar' 
                                            style={{ 
                                                width: `${percentage}%`,
                                                background: barColor
                                            }}
                                        >
                                            <span className='category-bar-revenue'>
                                                ${parseFloat(category.totalRevenue || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className='category-stats'>
                                    {category.invoiceCount} facturas - {parseFloat(category.totalQuantity || 0).toFixed(0)} unidades - {percentage}%
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}

export default SalesByCategory;
